import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  LayerType,
  LayerMetadata,
  StarsLayerMetadata,
  SongsLayerMetadata,
} from '../../types/layerMetadata';
import {
  MovesLayerMetadata,
  LooksLayerMetadata,
  WorldsLayerMetadata,
} from '../../types/referencelayerMetadata';
import StarsMetadataEditor from './StarsMetadataEditor';
import SongsMetadataEditor from './SongsMetadataEditor';
import MovesMetadataEditor from './MovesMetadataEditor';
import LooksMetadataEditor from './LooksMetadataEditor';
import WorldsMetadataEditor from './WorldsMetadataEditor';
import { getLayerDisplayName, getLayerIcon } from '../../utils/layerUtils';

interface LayerMetadataEditorProps {
  layer: LayerType;
  layerMetadata: LayerMetadata;
  isEditable?: boolean;
  onMetadataChange?: (updatedMetadata: LayerMetadata) => void;
  asset?: any;
  baseAssetData?: any;
  showProcessingFeedback?: boolean;
  processingState?: {
    stage: string;
    progress: number;
    currentField?: string;
    estimatedTime?: number;
  };
}

/**
 * Unified Layer Metadata Editor
 * Provides consistent interface for editing metadata across all layers (Stars, Looks, Worlds, Moves)
 *
 * Features:
 * - Layer-agnostic architecture
 * - Inheritance support for variant assets
 * - Real-time validation
 * - Processing feedback
 */
export const LayerMetadataEditor: React.FC<LayerMetadataEditorProps> = ({
  layer,
  layerMetadata,
  isEditable = true,
  onMetadataChange,
  asset,
  baseAssetData,
  showProcessingFeedback = false,
  processingState,
}) => {
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const LayerIcon = getLayerIcon(layer);
  const layerDisplayName = getLayerDisplayName(layer);

  // Filter out invalid fields from initial layerMetadata for Worlds layer
  const filteredLayerMetadata = React.useMemo(() => {
    if (layer === 'W' && layerMetadata) {
      const { genre, ...filtered } = layerMetadata as any;
      return filtered as LayerMetadata;
    }
    return layerMetadata;
  }, [layer, layerMetadata]);

  // Validate initial metadata on mount
  React.useEffect(() => {
    if (filteredLayerMetadata) {
      const errors = validateLayerMetadata(layer, filteredLayerMetadata);
      setValidationErrors(errors);
    }
  }, [layer, filteredLayerMetadata]);

  const handleMetadataChange = useCallback(
    (updatedMetadata: LayerMetadata) => {
      // Validate metadata based on layer-specific rules
      const errors = validateLayerMetadata(layer, updatedMetadata);
      setValidationErrors(errors);

      if (onMetadataChange) {
        onMetadataChange(updatedMetadata);
      }
    },
    [layer, onMetadataChange]
  );

  const renderLayerEditor = () => {
    switch (layer.toUpperCase()) {
      case 'S':
        return (
          <StarsMetadataEditor
            layerMetadata={filteredLayerMetadata as StarsLayerMetadata}
            isEditable={isEditable}
            onMetadataChange={handleMetadataChange}
            asset={asset}
            baseAssetData={baseAssetData}
          />
        );
      case 'L':
        return (
          <LooksMetadataEditor
            layerMetadata={filteredLayerMetadata as LooksLayerMetadata}
            isEditable={isEditable}
            onMetadataChange={handleMetadataChange}
            asset={asset}
            aiGeneratedDescription={asset?.aiMetadata?.data?.description}
          />
        );
      case 'W':
        return (
          <WorldsMetadataEditor
            layerMetadata={filteredLayerMetadata as WorldsLayerMetadata}
            isEditable={isEditable}
            onMetadataChange={handleMetadataChange}
            asset={asset}
            aiGeneratedDescription={asset?.aiMetadata?.data?.description}
          />
        );
      case 'M':
        return (
          <MovesMetadataEditor
            layerMetadata={filteredLayerMetadata as MovesLayerMetadata}
            isEditable={isEditable}
            onMetadataChange={handleMetadataChange}
            asset={asset}
            aiGeneratedDescription={asset?.aiMetadata?.data?.description}
          />
        );
      case 'G':
        return (
          <SongsMetadataEditor
            layerMetadata={filteredLayerMetadata as SongsLayerMetadata}
            isEditable={isEditable}
            onMetadataChange={handleMetadataChange}
            asset={asset ? { ...asset, layer: 'G' } : { layer: 'G' }}
            aiGeneratedDescription={asset?.aiMetadata?.data?.description}
          />
        );
      case 'C':
        return (
          <Alert severity="info">
            Composite Layer Editor - Existing Implementation
          </Alert>
        );
      default:
        return <Alert severity="error">Unsupported layer type: {layer}</Alert>;
    }
  };

  return (
    <Card elevation={2}>
      <CardHeader
        avatar={<LayerIcon color="primary" />}
        title={`${layerDisplayName} Layer Metadata`}
        subheader={`Edit metadata for ${layerDisplayName.toLowerCase()} layer`}
        action={
          <Chip label={layer} color="primary" size="small" variant="outlined" />
        }
      />

      {showProcessingFeedback && processingState && (
        <Box sx={{ p: 2, bgcolor: 'background.default' }}>
          <Typography variant="body2" gutterBottom>
            Processing {layerDisplayName} layer...
          </Typography>
          <CircularProgress
            variant="determinate"
            value={processingState.progress}
            size={20}
            sx={{ mr: 1 }}
          />
          <Typography variant="caption" color="text.secondary">
            {processingState.stage}
            {processingState.currentField &&
              ` - ${processingState.currentField}`}
          </Typography>
        </Box>
      )}

      {validationErrors.length > 0 && (
        <Alert severity="warning" sx={{ m: 2 }}>
          <Typography variant="body2">
            Validation issues: {validationErrors.join(', ')}
          </Typography>
        </Alert>
      )}

      <CardContent>{renderLayerEditor()}</CardContent>
    </Card>
  );
};

/**
 * Validate layer metadata based on layer-specific rules
 */
const validateLayerMetadata = (
  layer: LayerType,
  metadata: LayerMetadata
): string[] => {
  const errors: string[] = [];

  // Common validation rules
  if (!metadata) {
    errors.push('Metadata is required');
    return errors;
  }

  // For Worlds layer, filter out genre and targetAudience fields that don't belong
  // These fields might come from AI responses or legacy data but aren't part of Worlds API
  let metadataToValidate = metadata;
  if (layer === 'W') {
    const { genre, ...filteredMetadata } = metadata as any;
    metadataToValidate = filteredMetadata as LayerMetadata;
  }

  // Layer-specific validation
  switch (layer) {
    case 'G':
      // Songs layer - only fields actually required by SongMetadataRequest API
      const songNameValue = metadataToValidate.songName;
      if (!songNameValue || (typeof songNameValue === 'string' && songNameValue.trim() === '')) {
        errors.push('Song name is required');
      }
      const artistNameValue = metadataToValidate.artistName;
      if (!artistNameValue || (typeof artistNameValue === 'string' && artistNameValue.trim() === '')) {
        errors.push('Artist name is required');
      }
      // Genre and mood are arrays in API, check if they exist and have items
      if (!Array.isArray(metadataToValidate.genre) || metadataToValidate.genre.length === 0) {
        // Also check if it's a string that can be converted
        if (!metadataToValidate.genre || (typeof metadataToValidate.genre === 'string' && metadataToValidate.genre.trim() === '')) {
          errors.push('Genre is required');
        }
      }
      if (!Array.isArray(metadataToValidate.mood) || metadataToValidate.mood.length === 0) {
        // Also check if it's a string that can be converted
        if (!metadataToValidate.mood || (typeof metadataToValidate.mood === 'string' && metadataToValidate.mood.trim() === '')) {
          errors.push('Mood is required');
        }
      }
      // Tempo is required but may come from energy field or default to 'Medium'
      // Check both tempo and energy fields - handle both string and number types
      const tempoValue = metadataToValidate.tempo || (metadataToValidate as any).energy;
      if (!tempoValue || 
          (typeof tempoValue === 'string' && tempoValue.trim() === '') ||
          (typeof tempoValue === 'number' && (isNaN(tempoValue) || tempoValue <= 0))) {
        errors.push('Tempo is required (can be provided as tempo or energy field)');
      }
      break;
    case 'S':
      // Stars layer - match StarMetadataRequest (OpenAPI): starName, gender, ageGroup, ethnicity, mood
      if (!metadataToValidate.starName || metadataToValidate.starName.trim() === '') {
        errors.push('Star name is required');
      }
      if (!metadataToValidate.gender || metadataToValidate.gender.trim() === '') {
        errors.push('Gender is required');
      }
      if (!metadataToValidate.ageGroup || metadataToValidate.ageGroup.trim() === '') {
        errors.push('Age group is required');
      }
      if (!metadataToValidate.ethnicity || metadataToValidate.ethnicity.trim() === '') {
        errors.push('Ethnicity is required');
      }
      if (!Array.isArray(metadataToValidate.mood) || metadataToValidate.mood.length === 0) {
        errors.push('Mood is required');
      }
      break;
    case 'L':
      // Check all mandatory fields for Looks layer (matching backend API requirements)
      const looksRequiredFields = ['outfitName', 'styleCategory', 'style', 'occasion', 'colorScheme', 'primaryGarment', 'formality'];
      looksRequiredFields.forEach(field => {
        if (!metadataToValidate[field] || (typeof metadataToValidate[field] === 'string' && metadataToValidate[field].trim() === '')) {
          errors.push(`${field} is required for Looks layer`);
        }
      });
      // primaryColors is an array - check separately
      if (!Array.isArray(metadataToValidate.primaryColors) || metadataToValidate.primaryColors.length === 0) {
        errors.push('primaryColors is required for Looks layer');
      }
      break;
    case 'W':
      // Match WorldsMetadataRequest (OpenAPI): worldName, locationType, mood
      const worldsRequiredFields = ['worldName', 'locationType'];
      worldsRequiredFields.forEach(field => {
        if (!metadataToValidate[field] || (typeof metadataToValidate[field] === 'string' && metadataToValidate[field].trim() === '')) {
          errors.push(`${field} is required for Worlds layer`);
        }
      });
      if (!Array.isArray(metadataToValidate.mood) || metadataToValidate.mood.length === 0) {
        errors.push('mood is required for Worlds layer');
      }
      break;
    case 'M':
      // Check all mandatory fields for Moves layer (matching backend API requirements)
      // Only fields with @NotBlank annotation are required
      const movesRequiredFields = ['tempo', 'moveName', 'danceStyle', 'difficulty'];
      movesRequiredFields.forEach(field => {
        if (!metadataToValidate[field] || (typeof metadataToValidate[field] === 'string' && metadataToValidate[field].trim() === '')) {
          errors.push(`${field} is required for Moves layer`);
        }
      });
      break;
  }

  return errors;
};

export default LayerMetadataEditor;
